/* eslint-disable react-hooks/exhaustive-deps */
import React, {ReactNode, useCallback, useEffect, useReducer} from 'react';
import hashObject from 'object-hash';
import {errorCodesInfo} from '@/config/errorCodesInfo.config';
import defaultImage from '@/images/defaultImage.png';
import {createSearchParams, useNavigate, useSearchParams} from 'react-router-dom';
import _ from 'lodash';
import { DepartmentsHierarchy } from '@/types/ApiTypes';
import MarkdownEditor from '@uiw/react-markdown-editor';
import { DOMPurify } from './sanitizeHtml';

export function useBetterNavigate() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    return useCallback(
        (to, options = {}) => {
            // @ts-ignore
            let { params, search = params } = options;

            if (_.isNumber(to)) {
                return navigate(to);
            }

            if (_.isObject(search)) {
                search = createSearchParams(search ?? {}).toString();
            }

            if (_.isString(to)) {
                to = {
                    pathname: to,
                };
            }
            navigate(
                {
                    search,
                    ...to,
                },
                {
                    ...options,
                    // @ts-ignore
                    search: undefined,
                }
            );
        },
        [navigate, searchParams]
    );
}

/**
 * Hook that alerts clicks outside of the passed ref
 */
export function useClickOutsideHandler(ref, fn) {
    useEffect(() => {
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                fn?.();
            }
        }

        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);
}

export function camelCaseToTitleCase(s: string): string {
    const result = s.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
}

export function titleCaseToCamelCase(stringToConvert: string) {
    if (!stringToConvert) {
        return '';
    }
    
    const str = stringToConvert.trim();
    
    // Is it snake_case?
    if (str.includes('_')) {
        return '';
    }
    
    // Is it kebab-case?
    if (str.includes('-')) {
        return '';
    }

    // Handle single word case (convert to lowercase)
    if (!str.includes(' ')) {
        return str.toLowerCase();
    }
    
    const words = str.split(' ');
    
    const firstWord = words[0].toLowerCase();
    
    const restOfWords = words.slice(1).map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
    
    return [firstWord, ...restOfWords].join('');
}

export function snakeCaseToTitleCase(snakeCaseString?: string): string {
    if (!snakeCaseString) {
        return '';
    }
    return snakeCaseString.trim()
        .split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function renderMarkdownOrRawContent(content: ReactNode): JSX.Element {
    return typeof content === 'string' ? <MarkdownEditor.Markdown source={content}/> : <>{content}</>;
}

export const useSetState = (initialState) => {
    const [state, setState] = useReducer((state, newState) => ({ ...state, ...newState }), initialState);
    return [state, setState];
};

export const errorCodeDetails = (errorCode, formType) => {
    let lowerCaseErrorCode = errorCode ? errorCode.toLowerCase() : errorCode;
    let lowerCaseFormType = formType ? formType.toLowerCase() : formType;

    return errorCodesInfo[lowerCaseErrorCode]
        ? errorCodesInfo[lowerCaseErrorCode]
        : errorCodesInfo[lowerCaseFormType]
        ? errorCodesInfo[lowerCaseFormType]
        : errorCodesInfo['generic'];
};

export function arrayIntersection(arrays) {
    return arrays.reduce((intersection, array) => intersection.filter((v) => array.includes(v)), arrays[0]);
}

export function getDefaultImageSrc(event) {
    event.target.src = DOMPurify.sanitize(defaultImage);
}

export function oneDayLess(date) {
    var d = new Date(date);
    d.setDate(d.getDate() - 1);
    return d.toLocaleString().substring(0, 10);
}


export const convertStrToNum = (inputStr = '0') => {
    if(typeof inputStr === 'string') {
        return Number(inputStr.replace(/[^0-9]/g, ""));
    } 
    return inputStr;
}

export const  MrktEntitlementType = {
    PRIOR_APPROVAL              : "prior_approval",
    NOT_PRIOR_APPROVAL          : "not_prior_approval",
    PARTIAL_PRIOR_APPROVAL      : "partial_prior_approval",
    REV_SHARE                   : "rev_share",    
    NOT_REV_SHARE               : "not_rev_share",   
    CATEGORY_COVERED            : "category_covered",
    CATEGORY_NOT_COVERED        : "category_not_covered",
    DISABLE_STACK_BY_IN_DOD       : "disable_stackby",      
}

/**
 * Capitalize the first letter of the given string
 */
export const capitalize = (s) => {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
}

export function simulateMouseClick(element) {
    ['mousedown', 'click', 'mouseup'].forEach((mouseEventType) => {
        element.dispatchEvent(
            new MouseEvent(mouseEventType, {
                view: window,
                bubbles: true,
                cancelable: true,
                buttons: 1
            })
        )
    });
}

export function openChat() {
    const intercomChatElement = document.querySelector('div[class="intercom-chat"]');
    simulateMouseClick(intercomChatElement);
}

export const isValidArray = (value: unknown): value is any[] => {
    return Array.isArray(value) && value.length > 0;
};

export const arrayRange = (start, stop, step) =>
    Array.from({ length: (stop - start) / step + 1 }, (value, index) => start + index * step);

export function getObjectHash<T extends { [key: string]: any }>(object: T, excludeKeys: string[] = []): string {
    return hashObject(object, {
        excludeKeys(key: string) {
            return excludeKeys.includes(key);
        },
    });
}

export function listCategoriesForDepartment(department: string, hierarchyTree: DepartmentsHierarchy): string[] {
    if (!department || !hierarchyTree) return [];
    return Object.values(hierarchyTree?.[department.toUpperCase()])?.map(catSubCatGroup => Object.keys(catSubCatGroup ?? {})).flat();
}

export function printScreen() {
    window.print();
}

export const splitStringByDelimiter = (string: string, delimiter: string = ' | ') => {
    return string
        .split(delimiter)
        .map(string => string.trim())
        .filter(string => string !== '');
}

export function parseHiddenCharacters<TDelimit extends boolean, TReturn extends TDelimit extends true ? string : string[]>({clipboardData, returnAsDelimitedString = true as TDelimit, delimiter = " | "}: {clipboardData: string, returnAsDelimitedString?: TDelimit, delimiter?: " | " | " , " }): TReturn {

    const tempDelimiter = ' ;;; ';

    let parsedSearchString: string;

    let formattedSearchTerms: string[];

    parsedSearchString = clipboardData
        .replace(/\t/g, tempDelimiter) // replace tabs with tempDelimiter
        .replace(/\r/g, tempDelimiter) // replace carriage returns with tempDelimiter
        .replace(/\n/g, tempDelimiter); // replace new lines with tempDelimiter

    formattedSearchTerms = splitStringByDelimiter(parsedSearchString, tempDelimiter);

    if (returnAsDelimitedString) {
        return formattedSearchTerms.join(delimiter) as TReturn;
    } else {
        return formattedSearchTerms as TReturn;
    }
}

export function getRegexForSearchString(searchString = ''): RegExp {
    return new RegExp(searchString.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
}

export function parseSearchParams<TReturnObj extends object>(objectAsString?: string | null): TReturnObj | undefined {
    if (!objectAsString?.trim()) {
        return;
    }
    
    try {
        return JSON.parse(objectAsString);
    } catch (error) {
        console.error("Failed to parse provided data", {objectAsString, error});
        return;
    }
}

export function isMajorityNumeric(options: Array<{ value: string } | string>): boolean {
    if (options.length === 0) return false;

    const numericCount = options.filter((option) => {
        const textValue = typeof option === 'string' ? option : option.value;
        // Match values that numericSortByText can parse: optional </>  operator followed by a number
        // e.g. "12", "12.5", "<43", ">0.10", ".5", "<.75"
        return /^[<>]?(\d+(\.\d+)?|\.\d+)/.test(textValue.trim());
    }).length;

    const percentage = (numericCount / options.length) * 100;
    return percentage > 50;
}